/* PiHOT Track Order — <p-track-order> in sections/pihot-track-order.liquid.
   Posts { order, email } or { tracking } (tab) to the tracking API (services/pihot-track) and renders each
   shipment: status, milestones, carrier + tracking number, items and carrier events.
   Shipments that 17TRACK is still fetching are re-checked a few times automatically. */

(() => {
  if (customElements.get('p-track-order')) return;

  const ALERT = ['Exception', 'DeliveryFailure', 'Expired'];
  const IN_TRANSIT = ['InTransit', 'Expired', 'AvailableForPickup', 'OutForDelivery', 'DeliveryFailure', 'Delivered', 'Exception'];
  const NEAR = ['OutForDelivery', 'AvailableForPickup', 'DeliveryFailure', 'Delivered'];
  const RETRY_DELAYS = [8000, 20000, 45000];
  const EVENTS_COLLAPSED = 4;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PTrackOrder extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('[data-p-track-form]');
      this.result = this.querySelector('[data-p-track-result]');
      this.error = this.querySelector('[data-p-track-error]');
      if (!this.form || !this.result) return;

      this.button = this.form.querySelector('[type="submit"]');
      this.orderInput = this.form.elements.order;
      this.emailInput = this.form.elements.email;
      this.endpoint = this.dataset.endpoint;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.t = {};
      try {
        const decoder = document.createElement('textarea');
        const strings = JSON.parse(this.querySelector('[data-p-track-i18n]')?.textContent || '{}');
        for (const [key, value] of Object.entries(strings)) {
          decoder.innerHTML = value;
          this.t[key] = decoder.value;
        }
      } catch {
        this.t = {};
      }
      this.icons = {};
      this.querySelector('[data-p-track-icons]')?.content.querySelectorAll('[data-icon]').forEach((icon) => {
        this.icons[icon.dataset.icon] = icon.innerHTML;
      });

      this.trackingInput = this.form.elements.tracking;
      this.tabs = [...this.form.querySelectorAll('[data-p-track-tab]')];
      this.mode = 'order';

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.lookup();
      });
      this.form.addEventListener('input', (event) => {
        event.target.removeAttribute('aria-invalid');
        if (!this.error.hidden) this.showError('');
      });
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setMode(tab.dataset.pTrackTab, true));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const index = this.tabs.indexOf(tab);
          const next =
            event.key === 'Home' ? 0
            : event.key === 'End' ? this.tabs.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.setMode(this.tabs[next].dataset.pTrackTab);
          this.tabs[next].focus();
        });
      });
      this.result.addEventListener('click', (event) => this.onResultClick(event));

      const params = new URLSearchParams(window.location.search);
      const order = params.get('order');
      const email = params.get('email');
      const tracking = params.get('tracking');
      if (tracking && this.trackingInput) {
        this.trackingInput.value = tracking;
        this.setMode('tracking');
        this.lookup();
        return;
      }
      if (order) this.orderInput.value = order;
      if (email) this.emailInput.value = email;
      if (order && this.emailInput.value) this.lookup();
    }

    disconnectedCallback() {
      clearTimeout(this.retryTimer);
      this.controller?.abort();
    }

    setMode(mode, focusField = false) {
      if (!this.tabs.length || mode === this.mode) return;
      this.mode = mode;
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.pTrackTab === mode;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.form.querySelectorAll('[data-p-track-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.pTrackPanel !== mode;
      });
      this.showError('');
      if (focusField) (mode === 'tracking' ? this.trackingInput : this.orderInput).focus();
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    /* Validates the visible fields; returns the request body or null. */
    query() {
      if (this.mode === 'tracking') {
        const tracking = this.trackingInput.value.trim();
        const valid = /^[A-Za-z0-9][A-Za-z0-9\s-]{4,46}[A-Za-z0-9]$/.test(tracking);
        this.trackingInput.setAttribute('aria-invalid', String(!valid));
        if (!valid) {
          this.showError(this.t.errorTrackingInvalid);
          this.trackingInput.focus();
          return null;
        }
        return { tracking };
      }

      const order = this.orderInput.value.trim();
      const email = this.emailInput.value.trim();
      this.orderInput.setAttribute('aria-invalid', String(!order));
      this.emailInput.setAttribute('aria-invalid', String(!EMAIL.test(email)));
      if (!order || !EMAIL.test(email)) {
        this.showError(this.t.errorInvalid);
        (order ? this.emailInput : this.orderInput).focus();
        return null;
      }
      return { order, email };
    }

    async lookup({ attempt = 0, silent = false } = {}) {
      clearTimeout(this.retryTimer);
      let body = this.lastQuery;

      if (!silent) {
        body = this.query();
        if (!body) return;
        this.lastQuery = body;
        this.showError('');
        this.button.classList.add('loading');
        this.button.setAttribute('aria-busy', 'true');
      }

      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: this.controller.signal
        });
        if (!response.ok) {
          if (silent) return;
          const byTracking = 'tracking' in body;
          const key =
            {
              400: byTracking ? 'errorTrackingInvalid' : 'errorInvalid',
              404: byTracking ? 'errorTrackingNotFound' : 'errorNotFound',
              429: 'errorRate'
            }[response.status] || 'errorServer';
          this.showError(this.t[key]);
          if (response.status === 404) this.result.hidden = true;
          return;
        }
        const data = await response.json();
        this.render(data, silent);
        if (data.shipments.some((shipment) => shipment.state === 'pending') && attempt < RETRY_DELAYS.length) {
          this.retryTimer = setTimeout(() => this.lookup({ attempt: attempt + 1, silent: true }), RETRY_DELAYS[attempt]);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !silent) this.showError(this.t.errorServer);
      } finally {
        if (!silent) {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-busy');
        }
      }
    }

    onResultClick(event) {
      const again = event.target.closest('[data-p-track-again]');
      if (again) {
        clearTimeout(this.retryTimer);
        this.result.hidden = true;
        this.result.innerHTML = '';
        const field = this.mode === 'tracking' ? this.trackingInput : this.orderInput;
        field.value = '';
        this.form.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
        return;
      }

      const more = event.target.closest('[data-p-track-more]');
      if (more) {
        more.previousElementSibling.classList.remove('is-collapsed');
        more.remove();
        return;
      }

      const copy = event.target.closest('[data-p-track-copy]');
      if (copy && navigator.clipboard) {
        navigator.clipboard.writeText(copy.dataset.pTrackCopy).then(() => {
          copy.textContent = this.t.copied;
          clearTimeout(copy.resetTimer);
          copy.resetTimer = setTimeout(() => {
            copy.textContent = this.t.copy;
          }, 1600);
        });
      }
    }

    /* Dates ------------------------------------------------------------------ */

    /* Carrier times are shown in the event's own time zone (as written in the ISO
       string); Shopify times (UTC) in the visitor's time zone. */
    toDate(iso, wallClock) {
      if (!iso) return null;
      const match = wallClock && /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso);
      const date = match
        ? new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], +(match[4] || 0), +(match[5] || 0)))
        : new Date(iso);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    formatDate(iso, { wallClock = false, time = false } = {}) {
      const date = this.toDate(iso, wallClock);
      if (!date) return '';
      const options = { month: 'short', day: 'numeric' };
      if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
      if (time) Object.assign(options, { hour: 'numeric', minute: '2-digit' });
      if (wallClock) options.timeZone = 'UTC';
      try {
        return new Intl.DateTimeFormat(this.locale, options).format(date);
      } catch {
        return date.toLocaleDateString();
      }
    }

    formatRange(from, to) {
      const start = this.toDate(from, true);
      const end = this.toDate(to, true);
      if (!start) return '';
      if (!end || start.getTime() === end.getTime()) return this.formatDate(from, { wallClock: true });
      try {
        const format = new Intl.DateTimeFormat(this.locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
        return format.formatRange ? format.formatRange(start, end) : `${format.format(start)} – ${format.format(end)}`;
      } catch {
        return `${this.formatDate(from, { wallClock: true })} – ${this.formatDate(to, { wallClock: true })}`;
      }
    }

    /* Rendering -------------------------------------------------------------- */

    render(data, silent) {
      const { order, shipments = [], unshipped = [] } = data;
      const t = this.t;
      const title = order ? fill(t.order, { name: order.name }) : shipments[0]?.number;
      const meta = order ? fill(t.placed, { date: this.formatDate(order.createdAt) }) : t.number;
      const parts = [
        `<div class="p-track__order">
          <div>
            <p class="p-track__order-name">${escapeHtml(title)}</p>
            <p class="p-track__order-meta">${escapeHtml(meta)}</p>
          </div>
          <button type="button" class="p-link p-track__again" data-p-track-again>${escapeHtml(order ? t.another : t.anotherPackage)}</button>
        </div>`
      ];

      if (order?.cancelled) {
        parts.push(`<div class="p-track__card">${this.notice('info', t.cancelled)}</div>`);
      }

      shipments.forEach((shipment, index) => parts.push(this.shipment(shipment, index, shipments.length, order)));

      if (unshipped.length && !order?.cancelled) {
        parts.push(
          `<article class="p-track__card p-track__ship">
            ${this.notice('box', t.preparing, t.preparingText)}
            ${this.items(unshipped)}
          </article>`
        );
      }

      this.result.innerHTML = parts.join('');
      this.result.hidden = false;

      if (silent) return;
      if (!reduceMotion()) {
        [...this.result.children].forEach((child, index) => {
          child.classList.add('p-track__in');
          child.style.animationDelay = `${index * 80}ms`;
        });
      }
      this.result.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      this.result.focus({ preventScroll: true });
    }

    statusTitle(shipment) {
      const t = this.t;
      const track = shipment.state === 'ok' ? shipment.track : null;
      switch (track?.status) {
        case 'InfoReceived':
          return t.statusInfo;
        case 'InTransit':
          return track.subStatus === 'InTransit_CustomsProcessing' ? t.statusCustoms : t.stepTransit;
        case 'Expired':
          return t.statusExpired;
        case 'AvailableForPickup':
          return t.statusPickup;
        case 'OutForDelivery':
          return t.stepOut;
        case 'DeliveryFailure':
          return t.statusFailure;
        case 'Delivered':
          return t.stepDelivered;
        case 'Exception':
          return t.statusException;
        default:
          return t.stepShipped;
      }
    }

    shipment(shipment, index, count, order) {
      const t = this.t;
      const track = shipment.state === 'ok' ? shipment.track : null;
      const status = track?.status;
      const milestones = track?.milestones || {};
      const tone = status === 'Delivered' ? ' p-track__ship--delivered' : ALERT.includes(status) ? ' p-track__ship--alert' : '';

      let eta = '';
      if (status === 'Delivered') {
        const date = milestones.Delivered || track.events[0]?.time;
        if (date) eta = fill(t.deliveredOn, { date: this.formatDate(date, { wallClock: true }) });
      } else if (track?.eta) {
        eta = fill(t.eta, { date: this.formatRange(track.eta.from, track.eta.to) });
      } else if (shipment.shopifyEta) {
        eta = fill(t.eta, { date: this.formatDate(shipment.shopifyEta) });
      }

      const steps = [
        order && { label: t.stepOrdered, done: true, date: this.formatDate(order.createdAt) },
        { label: t.stepShipped, done: true, date: this.formatDate(shipment.shippedAt) },
        {
          label: t.stepTransit,
          done: IN_TRANSIT.includes(status),
          date: this.formatDate(milestones.PickedUp || milestones.Departure || milestones.Arrival, { wallClock: true })
        },
        {
          label: status === 'AvailableForPickup' ? t.statusPickup : t.stepOut,
          done: NEAR.includes(status),
          date: this.formatDate(milestones.OutForDelivery || milestones.AvailableForPickup, { wallClock: true })
        },
        { label: t.stepDelivered, done: status === 'Delivered', date: this.formatDate(milestones.Delivered, { wallClock: true }) }
      ].filter(Boolean);
      const current = steps.reduce((last, step, stepIndex) => (step.done ? stepIndex : last), 0);

      const carrier = track?.carrier || shipment.company;
      const notices = {
        pending: this.notice('clock', t.pending, t.pendingText, true),
        not_found: this.notice('truck', t.waiting, t.waitingText),
        error: this.notice('info', t.unavailable, t.unavailableText),
        no_tracking: this.notice('info', '', t.noTrackingText)
      };
      const link = shipment.url || track?.carrierUrl;

      return `<article class="p-track__card p-track__ship${tone}">
        <header class="p-track__ship-head">
          ${count > 1 ? `<p class="p-track__package">${escapeHtml(fill(t.package, { index: index + 1, count }))}</p>` : ''}
          <h2 class="p-track__status">${escapeHtml(this.statusTitle(shipment))}</h2>
          ${eta ? `<p class="p-track__eta">${escapeHtml(eta)}</p>` : ''}
        </header>
        <ol class="p-track__steps">
          ${steps
            .map(
              (step, stepIndex) => `<li class="p-track__step${step.done ? ' is-done' : ''}${stepIndex === current ? ' is-current' : ''}"${stepIndex === current ? ' aria-current="step"' : ''}>
                <span class="p-track__step-dot"></span>
                <span class="p-track__step-label">${escapeHtml(step.label)}</span>
                ${step.done && step.date ? `<span class="p-track__step-date">${escapeHtml(step.date)}</span>` : ''}
              </li>`
            )
            .join('')}
        </ol>
        ${notices[shipment.state] || ''}
        ${
          carrier || shipment.number
            ? `<dl class="p-track__meta">
                ${carrier ? `<div><dt>${escapeHtml(t.carrier)}</dt><dd>${escapeHtml(carrier)}</dd></div>` : ''}
                ${
                  shipment.number
                    ? `<div><dt>${escapeHtml(t.number)}</dt><dd><span>${escapeHtml(shipment.number)}</span>${
                        navigator.clipboard
                          ? `<button type="button" class="p-track__copy" data-p-track-copy="${escapeHtml(shipment.number)}">${escapeHtml(t.copy)}</button>`
                          : ''
                      }</dd></div>`
                    : ''
                }
              </dl>`
            : ''
        }
        ${track?.events?.length ? this.events(track.events) : ''}
        ${shipment.items?.length ? `<div><p class="p-track__label">${escapeHtml(t.items)}</p>${this.items(shipment.items)}</div>` : ''}
        ${
          link
            ? `<div class="p-track__links"><a class="p-link" href="${escapeHtml(link)}" target="_blank" rel="noopener">${escapeHtml(t.carrierLink)}</a></div>`
            : ''
        }
      </article>`;
    }

    events(events) {
      const t = this.t;
      const collapsed = events.length > EVENTS_COLLAPSED;
      return `<div>
        <p class="p-track__label">${escapeHtml(t.updates)}</p>
        <ol class="p-track__events${collapsed ? ' is-collapsed' : ''}">
          ${events
            .map((event) => {
              const meta = [this.formatDate(event.time, { wallClock: true, time: true }), event.location].filter(Boolean).join(' · ');
              return `<li class="p-track__event">
                <p class="p-track__event-text">${escapeHtml(event.text)}</p>
                ${meta ? `<p class="p-track__event-meta">${escapeHtml(meta)}</p>` : ''}
              </li>`;
            })
            .join('')}
        </ol>
        ${
          collapsed
            ? `<button type="button" class="p-link p-track__more" data-p-track-more>${escapeHtml(fill(t.showAll, { count: events.length }))}</button>`
            : ''
        }
      </div>`;
    }

    items(items) {
      return `<ul class="p-track__items">
        ${items
          .map(
            (item) => `<li class="p-track__item">
              <span class="p-track__item-media">${
                item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="48" height="48" loading="lazy">` : ''
              }</span>
              <div>
                <p class="p-track__item-title">${escapeHtml(item.title)}</p>
                <p class="p-track__item-meta">${escapeHtml([item.variant, `× ${item.quantity}`].filter(Boolean).join(' · '))}</p>
              </div>
            </li>`
          )
          .join('')}
      </ul>`;
    }

    notice(icon, title, text, loading = false) {
      return `<div class="p-track__notice${loading ? ' is-loading' : ''}">
        <span class="p-track__notice-icon">${this.icons[icon] || ''}</span>
        <div>
          ${title ? `<p class="p-track__notice-title">${escapeHtml(title)}</p>` : ''}
          ${text ? `<p class="p-track__notice-text">${escapeHtml(text)}</p>` : ''}
        </div>
      </div>`;
    }
  }

  customElements.define('p-track-order', PTrackOrder);
})();
